import api from './api';

export type TaskType = 
  | 'DENSITY_MEASUREMENT'
  | 'PROCTOR'
  | 'REBAR'
  | 'COMPRESSIVE_STRENGTH'
  | 'CYLINDER_PICKUP';

export type TaskStatus =
  | 'ASSIGNED'
  | 'IN_PROGRESS_TECH'
  | 'READY_FOR_REVIEW'
  | 'APPROVED'
  | 'REJECTED_NEEDS_FIX'
  | 'COULD_NOT_ACCESS';

/** Present on approve responses (Supabase) when the server generates/stores the report PDF. */
export interface ApprovalPdfResult {
  success?: boolean;
  skipped?: boolean;
  taskType?: string;
  saved?: boolean;
  savedPath?: string | null;
  fileName?: string | null;
  saveError?: string | null;
  error?: string;
}

export interface Task {
  id: number;
  projectId: number;
  taskType: TaskType;
  status: TaskStatus;
  assignedTechnicianId?: number;
  assignedTechnicianName?: string;
  assignedTechnicianEmail?: string;
  dueDate?: string;
  scheduledStartDate?: string;
  scheduledStartTime?: string;
  scheduledEndDate?: string;
  locationName?: string;
  locationNotes?: string;
  engagementNotes?: string;
  rejectionRemarks?: string;
  resubmissionDueDate?: string;
  // PM review fields (snake_case in API responses)
  pm_review_status?: 'NOT_STARTED' | 'REVIEWING' | 'COMPLETED' | string;
  pm_review_started_at?: string;
  pm_review_completed_at?: string;
  pm_reviewer_user_id?: number;
  fieldCompleted?: number;
  fieldCompletedAt?: string;
  reportSubmitted?: number;
  lastEditedByUserId?: number;
  lastEditedByRole?: string;
  lastEditedAt?: string;
  submittedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  projectNumber?: string;
  projectName?: string;
  proctorNo?: number;
  /** Per project + task type; 1 = unnumbered label, 2+ = "Rebar 2", etc. */
  workflowInstanceNo?: number | null;
  pdf?: ApprovalPdfResult | null;
  // Billing fields (task = dispatch)
  workorderId?: number | null;
  clockIn?: string | null;
  clockOut?: string | null;
  breakMinutes?: number;
  miles?: number;
  // QC screening result (set asynchronously when task reaches READY_FOR_REVIEW)
  qcResult?: { status: 'PASS' | 'FAIL' | 'ATTENTION' | 'SKIPPED'; flags: unknown[]; note?: string } | null;
  // AI-drafted PE narrative (set asynchronously when task reaches READY_FOR_REVIEW)
  peNotes?: string | null;
}

export interface ProctorTask {
  id: number;
  proctorNo: number;
  status: TaskStatus;
}

export interface CreateTaskRequest {
  projectId: number;
  taskType: TaskType;
  assignedTechnicianId?: number;
  workorderId?: number | null;
  dueDate?: string;
  scheduledStartDate?: string;
  scheduledStartTime?: string;
  scheduledEndDate?: string;
  locationName?: string;
  locationNotes?: string;
  engagementNotes?: string;
}

export interface UpdateTaskRequest {
  assignedTechnicianId?: number;
  workorderId?: number | null;
  dueDate?: string;
  scheduledStartDate?: string;
  scheduledStartTime?: string;
  scheduledEndDate?: string;
  locationName?: string;
  locationNotes?: string;
  engagementNotes?: string;
  taskType?: TaskType;
}

export interface UpdateTaskTimeRequest {
  clockIn?: string | null;
  clockOut?: string | null;
  breakMinutes?: number;
  miles?: number;
}

export interface RejectTaskRequest {
  rejectionRemarks: string;
  resubmissionDueDate: string;
}

export interface TaskHistoryEntry {
  id: number;
  taskId: number;
  timestamp: string;
  actorRole: string;
  actorName: string;
  actorUserId?: number;
  actionType: 'SUBMITTED' | 'APPROVED' | 'UNAPPROVED' | 'REJECTED' | 'REASSIGNED' | 'STATUS_CHANGED';
  note?: string;
}

const taskTypeLabels: { [key in TaskType]: string } = {
  DENSITY_MEASUREMENT: 'Density Measurement',
  PROCTOR: 'Proctor',
  REBAR: 'Rebar',
  COMPRESSIVE_STRENGTH: 'Compressive Strength',
  CYLINDER_PICKUP: 'Cylinder Pickup',
};

export const taskTypeLabel = (taskTypeOrTask: TaskType | Task): string => {
  // Support both old signature (taskType) and new signature (Task object)
  if (typeof taskTypeOrTask === 'string') {
    return taskTypeLabels[taskTypeOrTask] || taskTypeOrTask;
  }
  
  // New signature: Task object
  const task = taskTypeOrTask;
  const baseLabel = taskTypeLabels[task.taskType] || task.taskType;
  
  // For Proctor tasks, append the proctor number if available
  if (task.taskType === 'PROCTOR' && task.proctorNo) {
    return `${baseLabel} ${task.proctorNo}`;
  }

  const raw = task as Task & { workflow_instance_no?: number };
  const workflowInst = task.workflowInstanceNo ?? raw.workflow_instance_no;
  if (workflowInst != null && workflowInst > 1) {
    return `${baseLabel} ${workflowInst}`;
  }

  return baseLabel;
};

export const tasksAPI = {
  create: async (data: CreateTaskRequest): Promise<Task> => {
    const response = await api.post<Task>('/tasks', data);
    return response.data;
  },

  list: async (): Promise<Task[]> => {
    const response = await api.get<Task[]>('/tasks');
    return response.data;
  },

  getByProject: async (projectId: number): Promise<Task[]> => {
    const response = await api.get<Task[]>(`/tasks/project/${projectId}`);
    return response.data;
  },

  get: async (id: number): Promise<Task> => {
    const response = await api.get<Task>(`/tasks/${id}`);
    return response.data;
  },

  update: async (id: number, data: UpdateTaskRequest): Promise<Task> => {
    const response = await api.put<Task>(`/tasks/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<{ success: boolean }> => {
    const response = await api.delete<{ success: boolean }>(`/tasks/${id}`);
    return response.data;
  },

  updateStatus: async (id: number, status: TaskStatus): Promise<Task> => {
    const response = await api.put<Task>(`/tasks/${id}/status`, { status });
    return response.data;
  },

  approve: async (id: number): Promise<Task> => {
    const response = await api.post<Task>(`/tasks/${id}/approve`);
    return response.data;
  },

  getUnapproveContext: async (
    id: number
  ): Promise<{
    canUnapprove: boolean;
    autoSendEnabled: boolean;
    alreadySentToClient: boolean;
  }> => {
    const response = await api.get(`/tasks/${id}/unapprove-context`);
    return response.data;
  },

  unapprove: async (id: number, data: { note: string }): Promise<Task> => {
    const response = await api.post<Task>(`/tasks/${id}/unapprove`, data);
    return response.data;
  },

  reject: async (id: number, data: RejectTaskRequest): Promise<Task> => {
    const response = await api.post<Task>(`/tasks/${id}/reject`, data);
    return response.data;
  },

  bulkApprove: async (ids: number[]): Promise<{
    success: boolean;
    totals: {
      requested: number;
      approved: number;
      skippedWrongStatus: number;
      notFound: number;
      forbidden: number;
      errors: number;
    };
    results: Array<{
      id: number;
      status: string;
      currentStatus?: TaskStatus;
      error?: string;
      pdf?: ApprovalPdfResult | null;
    }>;
  }> => {
    const response = await api.post(`/tasks/bulk-approve`, { ids });
    return response.data;
  },

  // Dashboard filters
  getToday: async (): Promise<Task[]> => {
    const response = await api.get<Task[]>(`/tasks/dashboard/today?_=${Date.now()}`);
    return response.data;
  },

  getUpcoming: async (days: number = 7): Promise<Task[]> => {
    const response = await api.get<Task[]>(`/tasks/dashboard/upcoming?days=${days}&_=${Date.now()}`);
    return response.data;
  },

  getOverdue: async (): Promise<Task[]> => {
    const response = await api.get<Task[]>('/tasks/dashboard/overdue');
    return response.data;
  },

  getActivity: async (date: string): Promise<Task[]> => {
    const response = await api.get<Task[]>(`/tasks/dashboard/activity?date=${date}`);
    return response.data;
  },

  /** Tasks with field work marked complete (admin/PM activity log). */
  getCompletedFieldWork: async (): Promise<Task[]> => {
    const response = await api.get<Task[]>(`/tasks/dashboard/completed-field-work?_=${Date.now()}`);
    return response.data;
  },

  getPendingApproval: async (): Promise<Task[]> => {
    const response = await api.get<Task[]>(`/tasks/dashboard/pending-approval?_=${Date.now()}`);
    return response.data;
  },

  getHistory: async (taskId: number): Promise<TaskHistoryEntry[]> => {
    const response = await api.get<TaskHistoryEntry[]>(`/tasks/${taskId}/history`);
    return response.data;
  },

  // Technician dashboard views (filtered by assigned technician)
  /**
   * Browser-local calendar day YYYY-MM-DD (for technician dashboard ?asOf= with server).
   */
  getTechnicianCalendarAsOf: (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  },

  getTechnicianToday: async (asOf?: string): Promise<Task[]> => {
    const params = new URLSearchParams();
    params.set('_', String(Date.now()));
    if (asOf) params.set('asOf', asOf);
    const response = await api.get<Task[]>(`/tasks/dashboard/technician/today?${params.toString()}`);
    return response.data;
  },

  getTechnicianUpcoming: async (asOf?: string): Promise<Task[]> => {
    const params = new URLSearchParams();
    params.set('_', String(Date.now()));
    if (asOf) params.set('asOf', asOf);
    const response = await api.get<Task[]>(`/tasks/dashboard/technician/upcoming?${params.toString()}`);
    return response.data;
  },

  getTechnicianActivity: async (date: string): Promise<any[]> => {
    const response = await api.get<any[]>(`/tasks/dashboard/technician/activity?date=${date}`);
    return response.data;
  },

  /** Assigned tasks with field work marked complete (technician activity log). */
  getTechnicianCompletedFieldWork: async (): Promise<Task[]> => {
    const response = await api.get<Task[]>(
      `/tasks/dashboard/technician/completed-field-work?_=${Date.now()}`
    );
    return response.data;
  },

  // Tomorrow and Open Reports views
  getTechnicianTomorrow: async (asOf?: string): Promise<Task[]> => {
    const params = new URLSearchParams();
    params.set('_', String(Date.now()));
    if (asOf) params.set('asOf', asOf);
    const response = await api.get<Task[]>(`/tasks/dashboard/technician/tomorrow?${params.toString()}`);
    return response.data;
  },

  getTechnicianOpenReports: async (): Promise<Task[]> => {
    const response = await api.get<Task[]>(`/tasks/dashboard/technician/open-reports?_=${Date.now()}`);
    return response.data;
  },

  markFieldComplete: async (taskId: number): Promise<Task> => {
    const response = await api.post<Task>(`/tasks/${taskId}/mark-field-complete`);
    return response.data;
  },

  // Get Proctor tasks for a project (for Density form dropdown)
  getProctorsForProject: async (projectId: number): Promise<ProctorTask[]> => {
    const response = await api.get<ProctorTask[]>(`/tasks/project/${projectId}/proctors`);
    return response.data;
  },

  updateTime: async (id: number, data: UpdateTaskTimeRequest): Promise<Task> => {
    const response = await api.put<{ ok: boolean; task: Task }>(`/tasks/${id}/time`, data);
    return response.data.task;
  },

  savePeNotes: async (id: number, peNotes: string): Promise<void> => {
    await api.put(`/tasks/${id}/pe-notes`, { peNotes });
  },
};

